import { Button, HStack, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useMemo, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import { HookFormDropdown } from '$/components/core/Form/HookFormDropdown';
import { HookFormInput } from '$/components/core/Form/HookFormInput';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { updateStudentInvitation } from '$/services/usecases/invitation';
import { type StudentInvitationRequest } from '$/services/usecases/invitation/mapper/invitation';
import { type SchoolClass } from '$/services/usecases/school/mapper/schoolClasses';
import { emailFormatRegex } from '$/utils/regexUtils';

interface Props {
  invitation: StudentInvitationRequest;
  isOpen: boolean;
  onClose: () => void;
  schoolClassOptions: SchoolClass[];
}

type EditStudentInviteFormData = {
  email: string;
  firstName: string;
  lastName: string;
  schoolClassId: string;
};

export const EditStudentInvitationModal: FC<Props> = ({
  invitation,
  isOpen,
  onClose,
  schoolClassOptions,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { cloudMessageTranslation } = useCloudMessageTranslation();
  const schoolClassDropdown = useDisclosure();

  const schoolClassOptionLabels = useMemo(
    () =>
      schoolClassOptions.reduce<Record<string, string>>(
        (acc, schoolClass) => ({ ...acc, [schoolClass.id]: schoolClass.name }),
        {},
      ),
    [schoolClassOptions],
  );

  const formMethods = useForm<EditStudentInviteFormData>({
    mode: 'onTouched',
    values: {
      email: invitation.email,
      firstName: invitation.firstName,
      lastName: invitation.lastName,
      schoolClassId: invitation.schoolClassId,
    },
  });

  const onCloseModal = () => {
    onClose();
    formMethods.reset();
  };

  const { mutate: updateInvite, isPending } = useMutation({
    mutationFn: (data: EditStudentInviteFormData) =>
      updateStudentInvitation({
        id: invitation.id,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        schoolClassId: data.schoolClassId,
      }),
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }
      toast(t('school.students.editInvitationSuccess'), 'success', '');
      onCloseModal();
    },
  });

  return (
    <FullModal
      isOpen={isOpen}
      onClose={onCloseModal}
      header={t('school.students.editInvitationModal.title')}
      size='xl'
      isScrollable={false}
    >
      <FormProvider {...formMethods}>
        <Stack
          as='form'
          gap='4'
          onSubmit={formMethods.handleSubmit((data) => updateInvite(data))}
        >
          <HStack w='full'>
            <HookFormInput<EditStudentInviteFormData>
              accessor='firstName'
              label={t('dashboard.authentication.firstNameLabel')}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
            <HookFormInput<EditStudentInviteFormData>
              accessor='lastName'
              label={t('dashboard.authentication.lastNameLabel')}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
          </HStack>
          <HookFormInput<EditStudentInviteFormData>
            accessor='email'
            label={t('dashboard.authentication.emailLabel')}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
              pattern: {
                value: emailFormatRegex,
                message: t(
                  'dashboard.authentication.errorMessages.wrongFormat',
                ),
              },
            }}
          />
          <HookFormDropdown<EditStudentInviteFormData>
            accessor='schoolClassId'
            dropdownOpen={schoolClassDropdown.isOpen}
            toggleDropdown={schoolClassDropdown.onToggle}
            onBlur={schoolClassDropdown.onClose}
            label={t('school.schoolClasses.inviteStudent.classDropdownLabel')}
            placeholder={t(
              'school.schoolClasses.inviteStudent.classDropdownPlaceholder',
            )}
            valueLabels={schoolClassOptionLabels}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
            }}
            dropdownContent={() => (
              <Stack pt='2' pb='6px'>
                {schoolClassOptions.map((schoolClass) => (
                  <Button
                    key={schoolClass.id}
                    px='4'
                    py='6px'
                    fontSize='sm'
                    fontWeight='normal'
                    textAlign='left'
                    _hover={{ bg: 'bodyBackground' }}
                    cursor='pointer'
                    onClick={() => {
                      formMethods.setValue('schoolClassId', schoolClass.id, {
                        shouldDirty: true,
                      });
                      schoolClassDropdown.onClose();
                    }}
                    type='button'
                    variant='unstyled'
                  >
                    <HStack>
                      <Text>{schoolClass.name}</Text>
                    </HStack>
                  </Button>
                ))}
              </Stack>
            )}
          />
          <Text color='lighterText' fontSize='sm'>
            {t('school.students.editInvitationModal.emailChangeInfo')}
          </Text>
          <HStack justify='space-between' w='full' mt='10'>
            <Button
              isDisabled={isPending}
              onClick={onCloseModal}
              type='button'
              variant='ghost'
            >
              {t('general.cancel')}
            </Button>
            <Button
              isDisabled={
                !formMethods.formState.isValid || !formMethods.formState.isDirty
              }
              isLoading={isPending}
              type='submit'
            >
              {t('general.save')}
            </Button>
          </HStack>
        </Stack>
      </FormProvider>
    </FullModal>
  );
};
